import { useLocation, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Home, Search, ArrowLeft, Sparkles, Compass } from "lucide-react";
import { Button } from "@/components/ui/button";

const NotFound = () => {
  const location = useLocation();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20,
      });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  const quickLinks = [
    { label: "AI Tools", href: "/tools", icon: Sparkles },
    { label: "Compare Tools", href: "/compare", icon: Compass },
    { label: "Browse Categories", href: "/categories", icon: Search },
  ];

  return (
    <>
      <Helmet>
        <title>Page Not Found (404) | AI ToolHub</title>
        <meta name="description" content="The page you're looking for doesn't exist. Browse our AI tools, compare alternatives, or head back to the homepage." />
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-background via-background to-muted/30">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div 
            className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-pulse"
            style={{
              transform: `translate(${mousePosition.x}px, ${mousePosition.y}px)`,
              transition: "transform 0.3s ease-out",
            }}
          />
          <div 
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent/10 rounded-full blur-3xl animate-pulse"
            style={{
              animationDelay: "1s",
              transform: `translate(${-mousePosition.x}px, ${-mousePosition.y}px)`,
              transition: "transform 0.3s ease-out",
            }}
          />
          <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-primary/40 rounded-full animate-ping" />
          <div className="absolute top-1/3 right-1/3 w-3 h-3 bg-accent/40 rounded-full animate-ping" style={{ animationDelay: "0.5s" }} />
          <div className="absolute bottom-1/4 right-1/4 w-2 h-2 bg-primary/40 rounded-full animate-ping" style={{ animationDelay: "1s" }} />
        </div>

        <div className="relative z-10 flex min-h-screen flex-col items-center justify-center px-4 text-center">
          {/* Glitch-style 404 */}
          <div className="relative mb-8">
            <h1 
              className="text-[12rem] sm:text-[16rem] font-black leading-none tracking-tighter text-transparent bg-clip-text bg-gradient-to-br from-primary via-primary/80 to-accent select-none"
              style={{
                textShadow: "0 0 80px hsl(var(--primary) / 0.3)",
              }}
            >
              404
            </h1>
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-[12rem] sm:text-[16rem] font-black leading-none tracking-tighter text-primary/5 animate-pulse select-none">
                404
              </span>
            </div>
          </div>

          {/* Message */}
          <div className="max-w-lg space-y-4 mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
              Lost in the digital void
            </h2>
            <p className="text-muted-foreground text-lg">
              The page <code className="px-2 py-1 bg-muted rounded text-sm font-mono text-primary">{location.pathname}</code> doesn't exist or has been moved.
            </p>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button asChild size="lg" className="gap-2 px-8">
              <Link to="/">
                <Home className="h-5 w-5" />
                Go Home
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="gap-2 px-8">
              <Link to="#" onClick={() => window.history.back()}>
                <ArrowLeft className="h-5 w-5" />
                Go Back
              </Link>
            </Button>
          </div>

          {/* Quick links */}
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground uppercase tracking-wider font-medium">
              Popular destinations
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {quickLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="group flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 hover:bg-muted border border-border/50 hover:border-border transition-all duration-200 text-sm text-muted-foreground hover:text-foreground"
                >
                  <link.icon className="h-4 w-4 text-primary group-hover:scale-110 transition-transform" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Fun element */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
            <p className="text-xs text-muted-foreground/50">
              Error Code: 404 • Lost in cyberspace since {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default NotFound;
